import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Snackbar,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import FileTable from './fileList/FileTable';
import { useAuth } from '../contexts/AuthContext';
import { useFileListPage } from '../hooks/useFileListPage';
import { parseApiDateTime } from '../utils/apiDateTime';
import { getUserFacingApiErrorMessage } from '../utils/apiErrorMessage';

const FileList: React.FC = () => {
  const { user } = useAuth();
  const isReadOnlyUser = Boolean(user?.isViewer || user?.isExpired);
  const {
    files,
    uploadLimitsState,
    isLoading,
    isFetching,
    error,
    uploadSummary,
    deleteDialog,
    renameState,
    downloadState,
    mutations,
    actions,
  } = useFileListPage();

  const formatDateSafe = (dateString: string) => {
    if (!dateString) return '日付不明';
    try {
      const parsed = parseApiDateTime(dateString);
      if (!parsed) {
        return '無効な日付';
      }
      return format(parsed, 'yyyy年MM月dd日 HH:mm', { locale: ja });
    } catch (e) {
      return '無効な日付';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void actions.refetchFiles()} disabled={isFetching}>
            再試行
          </Button>
        }
      >
        {getUserFacingApiErrorMessage(error)}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Paper elevation={2} sx={{ p: 2, overflowX: 'auto' }}>
        <Typography variant="h5" gutterBottom sx={{ pl: 2, pt: 1 }}>
          アップロード済みファイル一覧
        </Typography>
        {uploadLimitsState.status === 'loaded' ? (
          <Typography variant="body2" sx={{ pl: 2, pb: 2 }}>
            {`ファイル数: ${uploadSummary.currentFileCount}件 / ${uploadSummary.maxUploadFiles}件、使用容量: ${uploadSummary.currentUsageMb.toFixed(2)}MB / ${uploadSummary.maxTotalMb}MB`}
          </Typography>
        ) : uploadLimitsState.status === 'error' ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                disabled={uploadLimitsState.isFetching}
                onClick={() => void actions.refetchUploadLimits()}
              >
                再試行
              </Button>
            }
          >
            アップロード制限を取得できませんでした。
          </Alert>
        ) : (
          <Typography variant="body2" sx={{ pl: 2, pb: 2 }}>
            アップロード制限を確認しています。
          </Typography>
        )}

        <FileTable
          files={files}
          editingFileId={renameState.editingFileId}
          editedFilename={renameState.editedFilename}
          renameError={renameState.renameError}
          isRenamePending={renameState.isPending}
          isDeletePending={mutations.delete.isPending}
          isDownloadingId={downloadState.isDownloadingId}
          onFilenameChange={actions.changeFilename}
          onConfirmRename={actions.confirmRename}
          onCancelRename={actions.cancelRename}
          onStartRename={actions.startRename}
          onViewDashboard={actions.viewDashboard}
          onDownload={actions.downloadFile}
          onOpenDeleteDialog={actions.openDeleteDialog}
          formatDate={formatDateSafe}
          isViewer={isReadOnlyUser}
        />

        {files.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body1" color="textSecondary">
              アップロードされたファイルはありません
            </Typography>
          </Box>
        )}
      </Paper>

      <Dialog
        open={deleteDialog.open}
        onClose={mutations.delete.isPending ? undefined : actions.closeDeleteDialog}
      >
        <DialogTitle>ファイル削除の確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteDialog.targetFilename
              ? `「${deleteDialog.targetFilename}」を本当に削除しますか？この操作は元に戻せません。`
              : 'このファイルを本当に削除しますか？この操作は元に戻せません。'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={actions.closeDeleteDialog} disabled={mutations.delete.isPending} autoFocus>キャンセル</Button>
          <Button
            onClick={() => {
              if (!isReadOnlyUser) {
                actions.deleteFile();
              }
            }}
            color="error"
            disabled={isReadOnlyUser || mutations.delete.isPending}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(deleteDialog.error)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={actions.deleteFile}
              disabled={mutations.delete.isPending}
            >
              再試行
            </Button>
          }
        >
          {deleteDialog.error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FileList;
